<script lang="ts">
  import { onMount } from 'svelte';
  import type {
    ActivityActionResult, ArtifactDefinition, ConstellationNodeId, CriticalTrainingEvent, DoctrineActionResult, DoctrineId, EconomyActionResult, GameState, ManualActionResult, PrestigeActionResult,
    PrestigeUpgradeId, RewardToken, SkillId
  } from '@rsvjelle/game-core';
  import { ACHIEVEMENTS, activitiesFor, allRealmMilestones, ARTIFACTS, artifactRarity, rewardDescription, SKILLS } from '@rsvjelle/game-core';
  import ActivitiesView from './components/ActivitiesView.svelte';
  import AscensionView from './components/AscensionView.svelte';
  import BottomNav from './components/BottomNav.svelte';
  import GameHeader from './components/GameHeader.svelte';
  import MoreView from './components/MoreView.svelte';
  import Onboarding from './components/Onboarding.svelte';
  import RealmLogo from './components/RealmLogo.svelte';
  import RealmRift from './components/RealmRift.svelte';
  import RealmView from './components/RealmView.svelte';
  import UpgradesView from './components/UpgradesView.svelte';
  import { formatNumber, titleCase } from './lib/display.ts';
  import { BrowserGameSession } from './lib/game-session.ts';
  import type { AccountSummary } from './lib/account-status.ts';
  import { checkForAppUpdate, downloadAppUpdate, installDownloadedUpdate, type AvailableAppUpdate } from './lib/app-update.ts';
  import {
    loadSettings, onboardingComplete, playerFeedback, saveSettings,
    setOnboardingComplete, type PlayerSettings
  } from './lib/player-settings.ts';

  type TabId = 'realm' | 'contracts' | 'upgrades' | 'ascension' | 'more';
  let gameState = $state<GameState | null>(null);
  let session = $state<BrowserGameSession | null>(null);
  let activeTab = $state<TabId>('realm');
  let bootError = $state('');
  let offlineSeconds = $state(0);
  let offlineGain = $state(0);
  let recoveredSave = $state(false);
  let showOnboarding = $state(false);
  let onboardingStep = $state(0);
  let onboardingSkill = $state<SkillId>('Woodcutting');
  let settings = $state<PlayerSettings>(loadSettings());
  let toasts = $state<Array<{ id: number; icon: string; title: string; detail: string; kind: string }>>([]);
  let toastId = 0;
  let account = $state<AccountSummary>({ status: 'checking' });
  let availableUpdate = $state<AvailableAppUpdate | null>(null);
  let updateBusy = $state(false);
  let updateProgress = $state(0);
  let updateMessage = $state('The APK downloads inside RSVjelle, then Android opens its installer.');
  let updateNeedsPermission = $state(false);
  let artifactReveal = $state<ArtifactDefinition | null>(null);
  const milestoneById = new Map(allRealmMilestones().map((milestone) => [milestone.id, milestone]));
  const achievementById = new Map(ACHIEVEMENTS.map((achievement) => [achievement.id, achievement]));

  function notify(icon: string, title: string, detail: string, kind: 'purchase' | 'level' | 'milestone' | 'ascension' = 'purchase'): void {
    const id = ++toastId;
    toasts = [...toasts, { id, icon, title, detail, kind }].slice(-3);
    playerFeedback(settings, kind);
    setTimeout(() => { toasts = toasts.filter((toast) => toast.id !== id); }, kind === 'ascension' ? 4400 : 3000);
  }

  onMount(() => {
    let disposed = false;
    let unsubscribe: (() => void) | null = null;
    const handleOfflineProgress = (event: Event) => {
      const result = (event as CustomEvent<{ simulatedSeconds: number; gainedResources: Record<string, number> }>).detail;
      offlineSeconds = result.simulatedSeconds;
      offlineGain = Object.values(result.gainedResources).reduce((sum, value) => sum + value, 0);
    };
    const handleCriticalTraining = (event: Event) => {
      for (const critical of (event as CustomEvent<CriticalTrainingEvent[]>).detail) showCritical(critical);
    };
    window.addEventListener('rsv2:offline-progress', handleOfflineProgress);
    window.addEventListener('rsv2:critical-training', handleCriticalTraining);
    if (new URLSearchParams(window.location.search).has('reset')) activeTab = 'more';
    void checkForAppUpdate().then((update) => { availableUpdate = update; }).catch(() => undefined);
    void BrowserGameSession.boot()
      .then((boot) => {
        if (disposed) { boot.session.destroy(); return; }
        session = boot.session;
        recoveredSave = boot.recoveredSave;
        offlineSeconds = boot.offline?.simulatedSeconds ?? 0;
        offlineGain = Object.values(boot.offline?.gainedResources ?? {}).reduce((sum, value) => sum + value, 0);
        showOnboarding = !onboardingComplete();
        if (showOnboarding) onboardingSkill = SKILLS[Math.floor(Math.random() * SKILLS.length)] ?? 'Woodcutting';
        unsubscribe = boot.session.subscribe((next) => {
          const previous = gameState;
          gameState = next;
          if (!previous) return;
          const known = new Set(previous.prestige.milestones ?? []);
          const newlyUnlocked = (next.prestige.milestones ?? []).filter((id) => !known.has(id));
          const knownAchievements = new Set(previous.prestige.achievements ?? []);
          const newAchievements = (next.prestige.achievements ?? []).filter((id) => !knownAchievements.has(id));
          const previousActivities = activitiesFor(previous);
          const nextActivities = activitiesFor(next);
          const previousContracts = new Map(previousActivities.contracts.map((contract) => [contract.id, contract]));
          const completedContracts = nextActivities.contracts.filter((contract) => contract.completed && !previousContracts.get(contract.id)?.completed);
          const milestoneSkills = new Set(newlyUnlocked.map((id) => milestoneById.get(id)?.skill).filter(Boolean));
          for (const skill of SKILLS) {
            if (next.run.skills[skill].level > previous.run.skills[skill].level && !milestoneSkills.has(skill)) {
              notify('⬆️', `${skill} level ${next.run.skills[skill].level}`, `+${next.run.skills[skill].level - previous.run.skills[skill].level} level${next.run.skills[skill].level - previous.run.skills[skill].level === 1 ? '' : 's'}`, 'level');
            }
          }
          for (const id of newlyUnlocked) {
            const milestone = milestoneById.get(id);
            if (milestone) notify(milestone.icon, milestone.title, milestone.description, 'milestone');
          }
          for (const id of newAchievements) {
            const achievement = achievementById.get(id);
            if (achievement) notify(achievement.icon, `Achievement · ${achievement.name}`, `${achievement.description} · +${achievement.points} pts`, 'milestone');
          }
          for (const contract of completedContracts) notify('📜', `Contract complete · ${contract.title}`, 'Its sealed reward is ready for your Vault.', 'milestone');
          if (!previousActivities.activeRift && nextActivities.activeRift) notify('◈', 'A Realm Rift appeared', 'Tap it before it fades to seal a rare reward.', 'milestone');
        });
      })
      .catch((error: unknown) => { bootError = error instanceof Error ? error.message : 'Could not open your realm.'; });
    return () => {
      disposed = true;
      window.removeEventListener('rsv2:offline-progress', handleOfflineProgress);
      window.removeEventListener('rsv2:critical-training', handleCriticalTraining);
      unsubscribe?.();
      session?.destroy();
    };
  });

  function selectSkill(skill: SkillId) {
    session?.selectSkill(skill);
    if (showOnboarding && onboardingStep === 1 && skill === onboardingSkill) onboardingStep = 2;
  }
  function manualAction(): ManualActionResult {
    if (!session) throw new Error('Game session is not ready');
    const result = session.manualAction();
    for (const critical of result.criticalEvents ?? []) showCritical(critical);
    playerFeedback(settings, 'tap');
    if (showOnboarding && onboardingStep === 2) onboardingStep = 3;
    return result;
  }
  function sell(resource: string, quantity: number): EconomyActionResult { return session!.sell(resource, quantity); }
  function buyTool(): EconomyActionResult { const result = session!.buyTool(); if (result.ok) notify('🛠️', 'Tool upgraded', `${gameState!.run.activeSkill} production is stronger.`, 'purchase'); return result; }
  function buyBuilding(): EconomyActionResult { const result = session!.buyBuilding(); if (result.ok) notify('🏛️', 'Hall upgraded', `A cumulative ×1.05 bonus was added.`, 'purchase'); return result; }
  function ascend(): PrestigeActionResult { const result = session!.ascend(); if (result.ok) notify('✦', 'Realm Ascended', `+${result.gainedShards} permanent Echo Shard${result.gainedShards === 1 ? '' : 's'}`, 'ascension'); return result; }
  function buyPrestige(upgrade: PrestigeUpgradeId): PrestigeActionResult { const result = session!.buyPrestigeUpgrade(upgrade); if (result.ok) notify('🌌', 'Permanent power gained', 'This upgrade survives every Ascension.', 'purchase'); return result; }
  function buyConstellation(node: ConstellationNodeId): PrestigeActionResult { const result = session!.buyConstellationNode(node); if (result.ok) notify('🌠', 'Constellation awakened', 'A permanent keystone now shapes every future run.', 'ascension'); return result; }
  function selectDoctrine(doctrine: DoctrineId, focusedSkill?: SkillId): DoctrineActionResult {
    const result = session!.selectDoctrine(doctrine, focusedSkill);
    if (result.ok) notify('🜂', 'Doctrine sworn', 'This build is locked until your next Ascension.', 'ascension');
    return result;
  }
  function rewardIcon(reward: RewardToken): string { return reward.kind === 'goldCache' ? '🪙' : reward.kind === 'supplyCache' ? '📦' : '⚡'; }
  function claimContract(contractId: string): ActivityActionResult {
    const result = session!.claimContract(contractId);
    if (result.ok && result.reward) notify(rewardIcon(result.reward), 'Reward sealed in Vault', `${rewardDescription(result.reward)} · use it whenever you choose.`, 'milestone');
    return result;
  }
  function useStoredReward(rewardId: string): ActivityActionResult {
    const result = session!.useStoredReward(rewardId);
    if (result.ok && result.reward) {
      const detail = result.gainedGold !== undefined
        ? `+${formatNumber(result.gainedGold)} gold`
        : result.gainedResources
          ? Object.entries(result.gainedResources).map(([resource, amount]) => `+${formatNumber(amount)} ${titleCase(resource)}`).join(' · ')
          : result.addedBoostSeconds !== undefined
            ? `×1.5 XP and resources for ${formatNumber(result.addedBoostSeconds / 60, 0)} minutes`
            : rewardDescription(result.reward);
      notify(rewardIcon(result.reward), 'Vault reward activated', detail, 'purchase');
    }
    return result;
  }
  function openRift(): ActivityActionResult {
    const result = session!.openRift();
    if (result.ok && result.reward) {
      notify('◈', 'Realm Rift sealed', `${rewardDescription(result.reward)} was stored in your Vault.`, 'milestone');
      if (result.artifactId) {
        const artifact = ARTIFACTS.find((item) => item.id === result.artifactId);
        if (artifact) setTimeout(() => { artifactReveal = artifact; playerFeedback(settings, 'ascension'); }, 450);
      } else if (result.duplicateArtifactId && result.gainedArtifactDust) {
        const artifact = ARTIFACTS.find((item) => item.id === result.duplicateArtifactId);
        notify('✨', 'Artifact resonance recovered', `${artifact?.name ?? 'Duplicate Artifact'} became +${result.gainedArtifactDust} Artifact Dust.`, 'milestone');
      }
    }
    return result;
  }
  function craftMissingArtifact(artifactId: string): ActivityActionResult {
    const result = session!.craftArtifact(artifactId);
    if (result.ok && result.artifactId) artifactReveal = ARTIFACTS.find((artifact) => artifact.id === result.artifactId) ?? null;
    return result;
  }
  function updateSettings(next: PlayerSettings): void { settings = next; saveSettings(next); }
  function finishOnboarding(): void { setOnboardingComplete(true); showOnboarding = false; onboardingStep = 0; }
  function restartOnboarding(): void {
    setOnboardingComplete(false);
    onboardingSkill = SKILLS[Math.floor(Math.random() * SKILLS.length)] ?? 'Woodcutting';
    showOnboarding = true;
    onboardingStep = 0;
    activeTab = 'realm';
  }

  function showCritical(event: CriticalTrainingEvent): void {
    if (event.kind === 'harvest') notify('💥', 'Critical harvest · ×8', `+${formatNumber(event.amount)} bonus ${titleCase(event.resource ?? 'resources')}`, 'milestone');
    else if (event.kind === 'insight') notify('🧠', 'Moment of insight · ×5', `+${formatNumber(event.amount)} bonus ${event.skill} XP`, 'milestone');
    else notify('⚡', 'Perfect strike', `+${formatNumber(event.amount)} bonus gold`, 'milestone');
  }
  function advanceOnboarding(): void {
    if (onboardingStep === 0) activeTab = 'realm';
    onboardingStep += 1;
  }
  function changeTab(tab: TabId): void {
    activeTab = tab;
    if (showOnboarding && onboardingStep === 3 && tab === 'contracts') onboardingStep = 4;
    else if (showOnboarding && onboardingStep === 5 && tab === 'upgrades') onboardingStep = 6;
    else if (showOnboarding && onboardingStep === 7 && tab === 'ascension') onboardingStep = 8;
  }
  function openWorkshop(): void { changeTab('upgrades'); }
  function openAccount(): void {
    activeTab = 'more';
    setTimeout(() => document.getElementById('account-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }
  async function beginAppUpdate(): Promise<void> {
    if (!availableUpdate || updateBusy) return;
    updateBusy = true;
    try {
      const status = updateNeedsPermission
        ? await installDownloadedUpdate()
        : await downloadAppUpdate(availableUpdate, (progress) => {
            updateProgress = progress.percent >= 0 ? progress.percent : updateProgress;
            updateMessage = progress.percent >= 0 ? `Downloading update… ${progress.percent}%` : 'Downloading update…';
          });
      if (status === 'permission_required') {
        updateNeedsPermission = true;
        updateMessage = 'Allow RSVjelle to install unknown apps, return here, then tap Continue installation.';
      } else {
        updateMessage = 'Android installer opened. Confirm Update to finish.';
      }
    } catch (error) {
      updateMessage = error instanceof Error ? error.message : 'The update could not be installed.';
    } finally {
      updateBusy = false;
    }
  }
</script>

{#if bootError}
  <main class="boot-screen"><div class="boot-card"><RealmLogo label="RSVjelle connection error" /><h1>Realm unavailable</h1><p>{bootError}</p><button onclick={() => location.reload()}>Try again</button></div></main>
{:else if !gameState || !session}
  <main class="boot-screen"><div class="boot-card"><RealmLogo animated /><p class="eyebrow">RESTORING YOUR REALM</p><h1>Awakening the echoes…</h1><div class="loading-track"><span></span></div></div></main>
{:else}
  <div class="app-shell">
    <GameHeader state={gameState} {account} onOpenAccount={openAccount} />
    <div class="app-layout">
      <BottomNav active={activeTab} contractAttention={activitiesFor(gameState).dormantRift || activitiesFor(gameState).contracts.some((contract) => contract.completed)} onChange={changeTab} />
      <main class="game-content">
        {#if activeTab === 'realm'}
          <RealmView gameState={gameState} onSelectSkill={selectSkill} onManualAction={manualAction} onBuyTool={buyTool} onBuyBuilding={buyBuilding} onOpenUpgrades={openWorkshop} {account} onOpenAccount={openAccount} />
        {:else if activeTab === 'contracts'}
          <ActivitiesView gameState={gameState} onClaim={claimContract} onUseReward={useStoredReward} onOpenRift={openRift} onCraft={craftMissingArtifact} />
        {:else if activeTab === 'upgrades'}
          <UpgradesView gameState={gameState} onSell={sell} onBuyTool={buyTool} onBuyBuilding={buyBuilding} onAssignPassiveSkills={(skills) => session!.assignPassiveSkills(skills)} onSelectSkill={selectSkill} />
        {:else if activeTab === 'ascension'}
          <AscensionView gameState={gameState} onAscend={ascend} onBuyUpgrade={buyPrestige} onBuyConstellation={buyConstellation} onChooseDoctrine={selectDoctrine} />
        {/if}
        <div class:hidden-view={activeTab !== 'more'}>
          <MoreView gameState={gameState} onSave={() => session!.flush()} onReplace={(state) => session!.replaceFromCloud(state)} onLink={(userId) => session!.linkAccount(userId)} snapshot={() => session!.snapshot()} {settings} onSettingsChange={updateSettings} onRestartOnboarding={restartOnboarding} onAccountStatusChange={(status) => { account = status; }} />
        </div>
      </main>
    </div>
  </div>

  {#if offlineSeconds >= 60 || recoveredSave}
    <div class="modal-backdrop" role="presentation">
      <div class="offline-modal" role="dialog" aria-modal="true" aria-labelledby="offline-title">
        <div class="offline-moon">☾</div><p class="eyebrow">WELCOME BACK</p><h2 id="offline-title">Your realm endured</h2>
        {#if recoveredSave}<p class="recovery-note">A damaged save was safely restored from your newest backup.</p>{/if}
        {#if offlineSeconds >= 60}
          <p>You were away for <strong>{formatNumber(offlineSeconds / 3600, 1)} hours</strong>.</p>
          {#if gameState.run.trainingActive === false}
            <p class="recovery-note">No skill was selected, so no resources were gathered. Choose a skill to begin training.</p>
          {:else}
            <div class="offline-reward"><span>Resources gathered</span><strong>+{formatNumber(offlineGain)}</strong></div>
          {/if}
          {#if activitiesFor(gameState).dormantRift}<p class="recovery-note">◈ One Dormant Rift is waiting in Contracts.</p>{/if}
        {/if}
        <button type="button" onclick={() => { offlineSeconds = 0; recoveredSave = false; }}>Enter the realm</button>
      </div>
    </div>
  {/if}

  {#if showOnboarding && offlineSeconds < 60 && !recoveredSave}
    <Onboarding step={onboardingStep} skill={onboardingSkill} onAdvance={advanceOnboarding} onComplete={finishOnboarding} />
  {/if}

  {#if artifactReveal}
    <div class="modal-backdrop artifact-reveal-backdrop" role="presentation">
      <div class:mythic={artifactRarity(artifactReveal) === 'mythic'} class:rare={artifactRarity(artifactReveal) === 'rare'} class="artifact-reveal" role="dialog" aria-modal="true" aria-labelledby="artifact-reveal-title">
        <div class="artifact-rays"></div>
        <p class="eyebrow">{artifactRarity(artifactReveal).toUpperCase()} DISCOVERY</p>
        <div class="artifact-reveal-icon">{artifactReveal.icon}</div>
        <h2 id="artifact-reveal-title">{artifactReveal.name}</h2>
        <p>{artifactReveal.description}</p>
        <button type="button" onclick={() => { artifactReveal = null; }}>Seal in the Codex</button>
      </div>
    </div>
  {/if}

  {#if availableUpdate}
    <div class="modal-backdrop update-backdrop" role="presentation">
      <div class="offline-modal update-modal" role="dialog" aria-modal="true" aria-labelledby="update-title">
        <div class="offline-moon">↻</div><p class="eyebrow">ANDROID UPDATE</p><h2 id="update-title">Version {availableUpdate.version} is ready</h2>
        <p>{updateMessage}</p>
        {#if updateBusy || updateProgress > 0}<div class="update-progress" aria-label={`Update download ${updateProgress}%`}><span style={`width:${updateProgress}%`}></span></div>{/if}
        <button type="button" onclick={() => void beginAppUpdate()} disabled={updateBusy}>{updateBusy ? 'Downloading…' : updateNeedsPermission ? 'Continue installation' : 'Download and update'}</button>
        <button class="text-button" type="button" onclick={() => { availableUpdate = null; }}>Not now</button>
      </div>
    </div>
  {/if}

  <div class="toast-stack" aria-live="polite">
    {#each toasts as toast (toast.id)}
      <div class:ascension={toast.kind === 'ascension'} class="realm-toast"><span>{toast.icon}</span><div><strong>{toast.title}</strong><small>{toast.detail}</small></div></div>
    {/each}
  </div>
  {@const realmActivities = activitiesFor(gameState)}
  {#if realmActivities.activeRift}
    <RealmRift rift={realmActivities.activeRift} onOpen={openRift} />
  {/if}
{/if}
